import { useState, useEffect } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Vote, Users, Star, Medal, Crown, Target, Shield, Flame, Award, Copy, Check, Plus, Eye, UserPlus, Trash2, Key, Swords, Timer, Bell, Send, Calendar, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIAS_ICONS = {
  esforco: Flame,
  tecnica: Target,
  equipe: Users,
  evolucao: Star,
  disciplina: Award,
  lideranca: Crown,
  fair_play: Medal,
  goleador: Trophy,
  defesa: Shield,
  criatividade: Star
};

export default function Arena({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [rankingEscola, setRankingEscola] = useState([]);
  const [rankingTreino, setRankingTreino] = useState([]);
  const [rankingJogo, setRankingJogo] = useState([]);
  const [medalhasSistema, setMedalhasSistema] = useState([]);
  const [codigoEscola, setCodigoEscola] = useState("");
  const [copiado, setCopiado] = useState(false);
  
  // Estados para criar votação
  const [showNovaVotacao, setShowNovaVotacao] = useState(false);
  const [tipoVotacao, setTipoVotacao] = useState("treino"); // treino ou jogo
  const [selectedTurma, setSelectedTurma] = useState("");
  const [selectedCategorias, setSelectedCategorias] = useState([]);
  const [alunosTurma, setAlunosTurma] = useState([]);
  const [alunosPresentes, setAlunosPresentes] = useState([]);
  const [adversario, setAdversario] = useState("");
  const [placarCasa, setPlacarCasa] = useState("");
  const [placarVisitante, setPlacarVisitante] = useState("");
  
  // Estados para ver resultados
  const [showResultados, setShowResultados] = useState(false);
  const [resultadosSessao, setResultadosSessao] = useState(null);
  
  // Estados para votar (professor)
  const [showVotar, setShowVotar] = useState(false);
  const [sessaoVotando, setSessaoVotando] = useState(null);
  const [votosProf, setVotosProf] = useState({});
  
  // Estados para gerenciar alunos
  const [alunosCadastrados, setAlunosCadastrados] = useState([]);
  const [alunosSemLogin, setAlunosSemLogin] = useState([]);
  const [showCriarLogin, setShowCriarLogin] = useState(false);
  const [selectedAlunoParaLogin, setSelectedAlunoParaLogin] = useState(null);
  const [novoUsername, setNovoUsername] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  
  // Hall da Fama
  const [hallDaFama, setHallDaFama] = useState(null);
  
  // Estados para Avisos (Coordenador)
  const [avisos, setAvisos] = useState([]);
  const [tiposAviso, setTiposAviso] = useState([]);
  const [showNovoAviso, setShowNovoAviso] = useState(false);
  const [avisoTipo, setAvisoTipo] = useState("aviso");
  const [avisoTitulo, setAvisoTitulo] = useState("");
  const [avisoMensagem, setAvisoMensagem] = useState("");
  const [avisoTurmas, setAvisoTurmas] = useState([]);
  const [avisoDataEvento, setAvisoDataEvento] = useState("");
  const [avisoHorario, setAvisoHorario] = useState("");
  const [avisoLocal, setAvisoLocal] = useState("");
  const [avisoRequerConfirmacao, setAvisoRequerConfirmacao] = useState(false);
  const [codigosVinculo, setCodigosVinculo] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriasRes, turmasRes, sessoesRes, rankingRes, rankingTreinoRes, rankingJogoRes, medalhasRes, codigoRes, cadastradosRes, semLoginRes, hallRes, avisosRes, tiposAvisoRes, codigosRes] = await Promise.all([
        api.get('/arena/categorias'),
        api.get('/turmas'),
        api.get('/arena/votacao/listar'),
        api.get('/arena/ranking/escola'),
        api.get('/arena/ranking/treino'),
        api.get('/arena/ranking/jogo'),
        api.get('/arena/medalhas/sistema'),
        api.get('/arena/escola/codigo'),
        api.get('/arena/alunos-cadastrados'),
        api.get('/arena/alunos-sem-login'),
        api.get('/arena/hall-da-fama'),
        api.get('/coordenador/avisos').catch(() => ({ data: [] })),
        api.get('/coordenador/tipos-aviso').catch(() => ({ data: [] })),
        api.get('/alunos/codigos-vinculo').catch(() => ({ data: [] }))
      ]);
      
      setCategorias(categoriasRes.data);
      setTurmas(turmasRes.data.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
      setSessoes(sessoesRes.data);
      setRankingEscola(rankingRes.data);
      setRankingTreino(rankingTreinoRes.data);
      setRankingJogo(rankingJogoRes.data);
      setMedalhasSistema(medalhasRes.data);
      setCodigoEscola(codigoRes.data.codigo);
      setAlunosCadastrados(cadastradosRes.data);
      setAlunosSemLogin(semLoginRes.data);
      setHallDaFama(hallRes.data);
      setAvisos(avisosRes.data);
      setTiposAviso(tiposAvisoRes.data);
      setCodigosVinculo(codigosRes.data);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const copiarCodigo = () => {
    navigator.clipboard.writeText(codigoEscola);
    setCopiado(true);
    toast.success("Código copiado!");
    setTimeout(() => setCopiado(false), 2000);
  };

  const handleSelectTurma = async (turmaId) => {
    setSelectedTurma(turmaId);
    try {
      const response = await api.get('/alunos');
      const alunosDaTurma = response.data.filter(a => 
        (a.turma_ids || []).includes(turmaId)
      ).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
      setAlunosTurma(alunosDaTurma);
      setAlunosPresentes(alunosDaTurma.map(a => a.id)); // Todos presentes por padrão
    } catch (error) {
      toast.error("Erro ao carregar alunos");
    }
  };

  const toggleCategoria = (catId) => {
    setSelectedCategorias(prev => 
      prev.includes(catId) 
        ? prev.filter(c => c !== catId)
        : [...prev, catId]
    );
  };

  const toggleAlunoPresente = (alunoId) => {
    setAlunosPresentes(prev =>
      prev.includes(alunoId)
        ? prev.filter(a => a !== alunoId)
        : [...prev, alunoId]
    );
  };

  const criarVotacao = async () => {
    if (!selectedTurma) {
      toast.error("Selecione uma turma");
      return;
    }
    if (selectedCategorias.length < 1) {
      toast.error("Selecione pelo menos 1 categoria");
      return;
    }
    if (alunosPresentes.length < 2) {
      toast.error("É necessário pelo menos 2 alunos presentes");
      return;
    }
    if (tipoVotacao === "jogo" && !adversario) {
      toast.error("Informe o time adversário");
      return;
    }

    try {
      const payload = {
        turma_id: selectedTurma,
        tipo: tipoVotacao,
        data_treino: new Date().toISOString().split('T')[0],
        categorias: selectedCategorias,
        alunos_presentes: alunosPresentes
      };

      if (tipoVotacao === "jogo") {
        payload.adversario = adversario;
        payload.placar_casa = placarCasa ? parseInt(placarCasa) : null;
        payload.placar_visitante = placarVisitante ? parseInt(placarVisitante) : null;
      }

      await api.post('/arena/votacao/criar', payload);
      
      toast.success(`Votação de ${tipoVotacao === 'jogo' ? 'Jogo' : 'Treino'} criada com sucesso!`);
      setShowNovaVotacao(false);
      resetFormVotacao();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar votação");
    }
  };

  const resetFormVotacao = () => {
    setTipoVotacao("treino");
    setSelectedTurma("");
    setSelectedCategorias([]);
    setAlunosTurma([]);
    setAlunosPresentes([]);
    setAdversario("");
    setPlacarCasa("");
    setPlacarVisitante("");
  };

  const verResultados = async (sessaoId) => {
    try {
      const response = await api.get(`/arena/votacao/${sessaoId}/resultados`);
      setResultadosSessao(response.data);
      setShowResultados(true);
    } catch (error) {
      toast.error("Erro ao carregar resultados");
    }
  };

  const iniciarVotacaoProf = (sessao) => {
    setSessaoVotando(sessao);
    setVotosProf({});
    setShowVotar(true);
  };

  const registrarVotoProf = async (categoriaId, alunoId) => {
    setVotosProf(prev => ({ ...prev, [categoriaId]: alunoId }));
  };

  const enviarVotosProf = async () => {
    if (!sessaoVotando) return;
    
    try {
      for (const [categoriaId, alunoId] of Object.entries(votosProf)) {
        await api.post('/arena/professor/votar', {
          sessao_id: sessaoVotando.id,
          categoria_id: categoriaId,
          votado_id: alunoId
        });
      }
      toast.success("Votos registrados com sucesso!");
      setShowVotar(false);
      setSessaoVotando(null);
      setVotosProf({});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao registrar votos");
    }
  };

  const fecharVotacao = async (sessaoId) => {
    if (!confirm("Tem certeza que deseja fechar esta votação?")) return;
    
    try {
      await api.post(`/arena/votacao/${sessaoId}/fechar`);
      toast.success("Votação fechada!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fechar votação");
    }
  };

  // Funções para gerenciar logins de alunos
  const abrirCriarLogin = (aluno) => {
    setSelectedAlunoParaLogin(aluno);
    // Sugerir username baseado no nome
    const nomeSimplificado = aluno.nome.split(' ')[0].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    setNovoUsername(nomeSimplificado);
    setNovaSenha("");
    setShowCriarLogin(true);
  };

  const criarLoginAluno = async () => {
    if (!selectedAlunoParaLogin || !novoUsername || !novaSenha) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    if (novaSenha.length < 4) {
      toast.error("A senha deve ter pelo menos 4 caracteres");
      return;
    }

    try {
      await api.post('/arena/professor/criar-login-aluno', {
        aluno_id: selectedAlunoParaLogin.id,
        username: novoUsername,
        password: novaSenha
      });
      
      toast.success(`Login criado para ${selectedAlunoParaLogin.nome}!`);
      setShowCriarLogin(false);
      setSelectedAlunoParaLogin(null);
      setNovoUsername("");
      setNovaSenha("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar login");
    }
  };

  const excluirLoginAluno = async (loginId, alunoNome) => {
    if (!confirm(`Tem certeza que deseja excluir o login de ${alunoNome}?`)) return;
    
    try {
      await api.delete(`/arena/professor/excluir-login/${loginId}`);
      toast.success("Login excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir login");
    }
  };

  // Funções para Avisos (Coordenador)
  const resetFormAviso = () => {
    setAvisoTipo("aviso");
    setAvisoTitulo("");
    setAvisoMensagem("");
    setAvisoTurmas([]);
    setAvisoDataEvento("");
    setAvisoHorario("");
    setAvisoLocal("");
    setAvisoRequerConfirmacao(false);
  };

  const enviarAviso = async () => {
    if (!avisoTitulo.trim() || !avisoMensagem.trim()) {
      toast.error("Preencha o título e a mensagem");
      return;
    }

    try {
      await api.post('/coordenador/aviso', {
        titulo: avisoTitulo,
        mensagem: avisoMensagem,
        tipo: avisoTipo,
        turma_ids: avisoTurmas.length > 0 ? avisoTurmas : null,
        data_evento: avisoDataEvento || null,
        horario: avisoHorario || null,
        local: avisoLocal || null,
        requer_confirmacao: avisoRequerConfirmacao
      });
      toast.success("Aviso enviado com sucesso!");
      setShowNovoAviso(false);
      resetFormAviso();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar aviso");
    }
  };

  const excluirAviso = async (avisoId) => {
    if (!confirm("Tem certeza que deseja excluir este aviso?")) return;
    
    try {
      await api.delete(`/coordenador/aviso/${avisoId}`);
      toast.success("Aviso excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir aviso");
    }
  };

  const toggleTurmaAviso = (turmaId) => {
    setAvisoTurmas(prev =>
      prev.includes(turmaId)
        ? prev.filter(t => t !== turmaId)
        : [...prev, turmaId]
    );
  };

  if (loading) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando Arena...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start sm:flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk' }}>
              🏟️ GGEE Arena
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">A arena digital da sua escola - Votações e Rankings</p>
          </div>
          
          {/* Código da Escola */}
          <Card className="border-purple-200 bg-purple-50 w-full sm:w-auto">
            <CardContent className="p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-purple-700 mb-2">Código da Escola (para alunos)</p>
              <div className="flex items-center gap-2">
                <code className="text-xl sm:text-2xl font-bold text-purple-800 tracking-wider">{codigoEscola}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={copiarCodigo}
                  className="text-purple-600"
                >
                  {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="votacoes" className="w-full">
          <TabsList className="flex w-full mb-4 sm:mb-6 overflow-x-auto gap-1 sm:gap-0 sm:grid sm:grid-cols-6 h-auto sm:h-10 p-1">
            <TabsTrigger value="votacoes" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">
              <Vote className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Votações</span>
            </TabsTrigger>
            <TabsTrigger value="ranking" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">
              <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Ranking</span>
            </TabsTrigger>
            <TabsTrigger value="hall" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">
              <Crown className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Hall</span>
            </TabsTrigger>
            <TabsTrigger value="alunos" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">
              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Alunos</span>
            </TabsTrigger>
            <TabsTrigger value="avisos" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">
              <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Avisos</span>
            </TabsTrigger>
            <TabsTrigger value="categorias" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">
              <Star className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Config</span>
            </TabsTrigger>
          </TabsList>

          {/* ========== ABA VOTAÇÕES ========== */}
          <TabsContent value="votacoes" className="space-y-4 sm:space-y-6">
            <div className="flex justify-end">
              <Button
                onClick={() => setShowNovaVotacao(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-xs sm:text-sm"
                data-testid="nova-votacao-btn"
              >
                <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Nova Votação de Treino</span>
                <span className="sm:hidden">Nova Votação</span>
              </Button>
            </div>

            {/* Votações Abertas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Vote className="w-5 h-5 text-green-600" />
                  Votações Abertas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sessoes.filter(s => s.status === 'aberta').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Nenhuma votação aberta</p>
                ) : (
                  <div className="space-y-3">
                    {sessoes.filter(s => s.status === 'aberta').map((sessao) => (
                      <div key={sessao.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-3 sm:p-4 border rounded-lg bg-green-50 border-green-200">
                        <div>
                          <h4 className="font-semibold text-sm sm:text-base">{sessao.turma_nome}</h4>
                          <p className="text-xs sm:text-sm text-gray-600">
                            <span className="block sm:inline">Data: {new Date(sessao.data_treino).toLocaleDateString('pt-BR')}</span>
                            <span className="hidden sm:inline"> • </span>
                            <span className="block sm:inline">{sessao.categorias.length} cat. • {sessao.alunos_presentes.length} alunos</span>
                          </p>
                        </div>
                        <div className="flex gap-1 sm:gap-2 flex-wrap justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => iniciarVotacaoProf(sessao)}
                            className="text-xs sm:text-sm px-2 sm:px-3"
                          >
                            <Vote className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            Votar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => verResultados(sessao.id)}
                            className="text-xs sm:text-sm px-2 sm:px-3"
                          >
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            Ver
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => fecharVotacao(sessao.id)}
                            className="bg-red-500 hover:bg-red-600 text-xs sm:text-sm px-2 sm:px-3"
                          >
                            Fechar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Votações Fechadas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-600" />
                  Votações Encerradas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sessoes.filter(s => s.status === 'fechada').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Nenhuma votação encerrada</p>
                ) : (
                  <div className="space-y-3">
                    {sessoes.filter(s => s.status === 'fechada').slice(0, 10).map((sessao) => (
                      <div key={sessao.id} className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center p-3 sm:p-4 border rounded-lg">
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold break-words">{sessao.turma_nome}</h4>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {new Date(sessao.data_treino).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => verResultados(sessao.id)}
                          className="shrink-0 w-full sm:w-auto"
                        >
                          <Trophy className="w-4 h-4 mr-1" />
                          Ver Resultados
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== ABA RANKING ========== */}
          <TabsContent value="ranking" className="space-y-6">
            <Tabs defaultValue="geral" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="geral" className="flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Geral
                </TabsTrigger>
                <TabsTrigger value="treinos" className="flex items-center gap-2">
                  <Timer className="w-4 h-4" />
                  Treinos
                </TabsTrigger>
                <TabsTrigger value="jogos" className="flex items-center gap-2">
                  <Swords className="w-4 h-4" />
                  Jogos
                </TabsTrigger>
              </TabsList>

              {/* Ranking Geral */}
              <TabsContent value="geral">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-amber-500" />
                      Ranking Geral da Escola
                    </CardTitle>
                    <CardDescription>
                      Pontuação acumulada de treinos e jogos
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {rankingEscola.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        Nenhum ranking ainda. Crie votações para começar!
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {rankingEscola.map((aluno, index) => (
                          <div 
                            key={aluno.aluno_id}
                            className={`flex items-center justify-between p-4 rounded-lg ${
                              index === 0 ? 'bg-amber-100 border-2 border-amber-400' :
                              index === 1 ? 'bg-gray-100 border-2 border-gray-400' :
                              index === 2 ? 'bg-orange-100 border-2 border-orange-400' :
                              'bg-gray-50 border'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                                index === 0 ? 'bg-amber-500 text-white' :
                                index === 1 ? 'bg-gray-400 text-white' :
                                index === 2 ? 'bg-orange-500 text-white' :
                                'bg-gray-300 text-gray-700'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <h4 className="font-semibold text-lg">{aluno.aluno_nome}</h4>
                                <div className="flex gap-3 text-sm text-gray-600">
                                  <span>🏆 {aluno.vitorias_categorias} vitórias</span>
                                  <span>👍 {aluno.votos_recebidos} votos</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-purple-600">{aluno.pontos_total}</div>
                              <div className="text-xs text-gray-500">pontos</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Ranking de Treinos */}
              <TabsContent value="treinos">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Timer className="w-5 h-5 text-purple-500" />
                      Ranking de Treinos
                    </CardTitle>
                    <CardDescription>
                      Pontuação acumulada apenas em treinos
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {rankingTreino.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        Nenhum ranking de treino ainda. Faça chamadas e votações!
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {rankingTreino.map((aluno, index) => (
                          <div 
                            key={aluno.aluno_id}
                            className={`flex items-center justify-between p-4 rounded-lg ${
                              index === 0 ? 'bg-purple-100 border-2 border-purple-400' :
                              index === 1 ? 'bg-gray-100 border-2 border-gray-400' :
                              index === 2 ? 'bg-purple-50 border-2 border-purple-200' :
                              'bg-gray-50 border'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                                index === 0 ? 'bg-purple-500 text-white' :
                                index === 1 ? 'bg-gray-400 text-white' :
                                index === 2 ? 'bg-purple-300 text-white' :
                                'bg-gray-300 text-gray-700'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <h4 className="font-semibold text-lg">{aluno.aluno_nome}</h4>
                                <div className="flex gap-3 text-sm text-gray-600">
                                  <span>⚽ {aluno.participacoes || 0} treinos</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-purple-600">{aluno.pontos_treino}</div>
                              <div className="text-xs text-gray-500">pontos</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Ranking de Jogos */}
              <TabsContent value="jogos">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Swords className="w-5 h-5 text-orange-500" />
                      Ranking de Jogos Amistosos
                      <Badge className="bg-orange-500 text-xs">2x pontos</Badge>
                    </CardTitle>
                    <CardDescription>
                      Pontuação acumulada em jogos (pontos em dobro!)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {rankingJogo.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        Nenhum ranking de jogo ainda. Crie votações de jogos amistosos!
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {rankingJogo.map((aluno, index) => (
                          <div 
                            key={aluno.aluno_id}
                            className={`flex items-center justify-between p-4 rounded-lg ${
                              index === 0 ? 'bg-orange-100 border-2 border-orange-400' :
                              index === 1 ? 'bg-gray-100 border-2 border-gray-400' :
                              index === 2 ? 'bg-orange-50 border-2 border-orange-200' :
                              'bg-gray-50 border'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                                index === 0 ? 'bg-orange-500 text-white' :
                                index === 1 ? 'bg-gray-400 text-white' :
                                index === 2 ? 'bg-orange-300 text-white' :
                                'bg-gray-300 text-gray-700'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <h4 className="font-semibold text-lg">{aluno.aluno_nome}</h4>
                                <div className="flex gap-3 text-sm text-gray-600">
                                  <span>🏆 {aluno.jogos_participados || 0} jogos</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-orange-600">{aluno.pontos_jogo}</div>
                              <div className="text-xs text-gray-500">pontos</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ========== ABA HALL DA FAMA ========== */}
          <TabsContent value="hall" className="space-y-6">
            {hallDaFama ? (
              <>
                {/* Destaques da Semana */}
                <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-500" />
                      Destaques da Semana
                    </CardTitle>
                    <CardDescription>
                      Semana iniciada em {hallDaFama.periodo?.semana_inicio}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {hallDaFama.destaques_semana?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">
                        Nenhum destaque esta semana ainda. As premiações são automáticas após cada chamada!
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {hallDaFama.destaques_semana?.map((destaque, index) => (
                          <div 
                            key={destaque.aluno_id}
                            className={`p-4 rounded-lg text-center ${
                              index === 0 ? 'bg-amber-100 border-2 border-amber-400' :
                              index === 1 ? 'bg-gray-100 border-2 border-gray-300' :
                              'bg-orange-50 border border-orange-200'
                            }`}
                          >
                            <div className={`text-4xl mb-2 ${
                              index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'
                            }`}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                            </div>
                            <img 
                              src={destaque.avatar_url} 
                              alt={destaque.aluno_nome}
                              className="w-16 h-16 rounded-full mx-auto mb-2 border-2 border-white shadow"
                            />
                            <h4 className="font-bold">{destaque.aluno_nome}</h4>
                            <p className="text-sm text-purple-600 font-semibold">
                              {destaque.vitorias} vitórias
                            </p>
                            {destaque.medalhas_count > 0 && (
                              <Badge className="mt-1 bg-amber-500">
                                <Medal className="w-3 h-3 mr-1" />
                                {destaque.medalhas_count} medalhas
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Campeões por Categoria */}
                {hallDaFama.campeoes_categorias?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Crown className="w-5 h-5 text-purple-500" />
                        Campeões por Categoria (Mês)
                      </CardTitle>
                      <CardDescription>
                        Quem mais venceu cada categoria em {hallDaFama.periodo?.mes}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {hallDaFama.campeoes_categorias?.map((campeao) => (
                          <div 
                            key={campeao.categoria_id}
                            className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center"
                          >
                            <div className="text-2xl mb-2">
                              {campeao.categoria_id === 'esforco' ? '🔥' :
                               campeao.categoria_id === 'tecnica' ? '🎯' :
                               campeao.categoria_id === 'evolucao' ? '📈' :
                               campeao.categoria_id === 'equipe' ? '🤝' :
                               campeao.categoria_id === 'disciplina' ? '📋' :
                               campeao.categoria_id === 'lideranca' ? '👑' : '⭐'}
                            </div>
                            <h5 className="font-semibold text-purple-800">{campeao.categoria_nome}</h5>
                            <img 
                              src={campeao.avatar_url} 
                              alt={campeao.aluno_nome}
                              className="w-12 h-12 rounded-full mx-auto my-2 border-2 border-purple-300"
                            />
                            <p className="text-sm font-medium">{campeao.aluno_nome}</p>
                            <p className="text-xs text-gray-500">{campeao.vitorias} vitórias</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Campeão de Medalhas */}
                {hallDaFama.campeao_medalhas && (
                  <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Medal className="w-5 h-5 text-amber-500" />
                        Maior Colecionador de Medalhas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-6">
                        <img 
                          src={hallDaFama.campeao_medalhas.avatar_url} 
                          alt={hallDaFama.campeao_medalhas.aluno_nome}
                          className="w-20 h-20 rounded-full border-4 border-amber-400 shadow-lg"
                        />
                        <div>
                          <h4 className="text-xl font-bold text-purple-800">
                            {hallDaFama.campeao_medalhas.aluno_nome}
                          </h4>
                          <p className="text-amber-600 font-semibold">
                            {hallDaFama.campeao_medalhas.medalhas_count} medalhas conquistadas!
                          </p>
                          <div className="flex gap-1 mt-2">
                            {hallDaFama.campeao_medalhas.medalhas?.slice(0, 5).map((medalhaId) => {
                              const medalhaInfo = medalhasSistema.find(m => m.id === medalhaId);
                              return medalhaInfo ? (
                                <Badge key={medalhaId} className={`text-xs ${
                                  medalhaInfo.cor === 'ouro' ? 'bg-amber-500' :
                                  medalhaInfo.cor === 'prata' ? 'bg-gray-400' :
                                  medalhaInfo.cor === 'bronze' ? 'bg-orange-600' : 'bg-purple-500'
                                }`}>
                                  {medalhaInfo.nome}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Estatísticas do Período */}
                <Card>
                  <CardHeader>
                    <CardTitle>Estatísticas do Período</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-3xl font-bold text-blue-600">
                          {hallDaFama.total_premiacoes_semana || 0}
                        </div>
                        <p className="text-sm text-gray-600">Premiações esta semana</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-3xl font-bold text-green-600">
                          {hallDaFama.total_premiacoes_mes || 0}
                        </div>
                        <p className="text-sm text-gray-600">Premiações este mês</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <Crown className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    O Hall da Fama será preenchido automaticamente após as chamadas!
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    As premiações acontecem automaticamente quando você faz a chamada da turma.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ========== ABA ALUNOS ========== */}
          <TabsContent value="alunos" className="space-y-6">
            {/* Card com link para alunos */}
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-purple-600" />
                  Link para os Alunos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Compartilhe este link com os alunos para eles acessarem a Arena:
                </p>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                  <code className="flex-1 text-purple-800 font-mono">
                    {window.location.origin}/aluno
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/aluno`);
                      toast.success("Link copiado!");
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  O aluno vai precisar do código: <strong>{codigoEscola}</strong>
                </p>
              </CardContent>
            </Card>

            {/* Alunos sem login */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-green-600" />
                  Criar Login para Alunos ({alunosSemLogin.length})
                </CardTitle>
                <CardDescription>
                  Alunos que ainda não possuem acesso à Arena
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alunosSemLogin.length === 0 ? (
                  <p className="text-center py-4 text-gray-500">
                    Todos os alunos já possuem login 🎉
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {alunosSemLogin.map((aluno) => (
                      <div
                        key={aluno.id}
                        className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <span className="font-medium">{aluno.nome}</span>
                        <Button
                          size="sm"
                          onClick={() => abrirCriarLogin(aluno)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Criar Login
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Alunos com login */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Alunos com Acesso ({alunosCadastrados.length})
                </CardTitle>
                <CardDescription>
                  Alunos que já podem acessar a Arena
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alunosCadastrados.length === 0 ? (
                  <p className="text-center py-4 text-gray-500">
                    Nenhum aluno cadastrado ainda
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {alunosCadastrados.map((login) => (
                      <div
                        key={login.id}
                        className="flex justify-between items-center p-3 border rounded-lg"
                      >
                        <div>
                          <span className="font-medium">{login.aluno_nome}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            (@{login.username})
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => excluirLoginAluno(login.id, login.aluno_nome)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== ABA AVISOS (COORDENADOR) ========== */}
          <TabsContent value="avisos" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Central de Avisos</h2>
                <p className="text-sm text-gray-500">Envie comunicados para os responsáveis</p>
              </div>
              <Button
                onClick={() => setShowNovoAviso(true)}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="novo-aviso-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Novo Aviso
              </Button>
            </div>

            {/* Lista de Avisos Enviados */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  Avisos Enviados
                </CardTitle>
                <CardDescription>
                  Histórico de comunicados da sua escola
                </CardDescription>
              </CardHeader>
              <CardContent>
                {avisos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    Nenhum aviso enviado ainda. Clique em "Novo Aviso" para começar!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {avisos.map((aviso) => (
                      <div key={aviso.id} className="flex justify-between items-start p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex gap-3">
                          <span className="text-2xl">
                            {aviso.tipo === 'treino' ? '⚽' : 
                             aviso.tipo === 'jogo' ? '🏆' :
                             aviso.tipo === 'evento' ? '🎉' :
                             aviso.tipo === 'cancelamento' ? '❌' :
                             aviso.tipo === 'reuniao' ? '👥' : '📢'}
                          </span>
                          <div>
                            <h4 className="font-semibold">{aviso.titulo}</h4>
                            <p className="text-sm text-gray-600 line-clamp-2">{aviso.mensagem}</p>
                            <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(aviso.created_at).toLocaleDateString('pt-BR')}
                              </span>
                              {aviso.data_evento && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Evento: {aviso.data_evento}
                                </span>
                              )}
                              {aviso.local && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {aviso.local}
                                </span>
                              )}
                            </div>
                            {aviso.requer_confirmacao && (
                              <Badge variant="outline" className="mt-2">
                                {aviso.estatisticas?.confirmados || 0} confirmações
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => excluirAviso(aviso.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Códigos de Vinculação */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-green-600" />
                  Códigos de Vinculação (Responsáveis)
                </CardTitle>
                <CardDescription>
                  Compartilhe estes códigos com os pais para que vinculem seus filhos
                </CardDescription>
              </CardHeader>
              <CardContent>
                {codigosVinculo.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    Os códigos serão gerados automaticamente para os alunos cadastrados
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {codigosVinculo.map((item) => (
                      <div key={item.aluno_id} className="flex justify-between items-center p-3 border rounded-lg bg-gray-50">
                        <span className="font-medium">{item.aluno_nome}</span>
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono bg-white px-2 py-1 rounded border">{item.codigo}</code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(item.codigo);
                              toast.success(`Código de ${item.aluno_nome} copiado!`);
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== ABA CATEGORIAS ========== */}
          <TabsContent value="categorias" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Categorias de Votação Disponíveis</CardTitle>
                <CardDescription>
                  Escolha até 3 categorias ao criar uma votação de treino
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categorias.map((cat) => {
                    const Icon = CATEGORIAS_ICONS[cat.id] || Star;
                    return (
                      <div key={cat.id} className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{cat.nome}</h4>
                          <p className="text-sm text-gray-600">{cat.descricao}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog Criar Login para Aluno */}
        <Dialog open={showCriarLogin} onOpenChange={setShowCriarLogin}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar Login para Aluno</DialogTitle>
              <DialogDescription>
                {selectedAlunoParaLogin?.nome}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome de Usuário</Label>
                <Input
                  value={novoUsername}
                  onChange={(e) => setNovoUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                  placeholder="Ex: joao123"
                />
                <p className="text-xs text-gray-500">Sem espaços, letras minúsculas</p>
              </div>
              
              <div className="space-y-2">
                <Label>Senha</Label>
                <Input
                  type="password"
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  placeholder="Mínimo 4 caracteres"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCriarLogin(false)}>
                Cancelar
              </Button>
              <Button onClick={criarLoginAluno} className="bg-green-600 hover:bg-green-700">
                Criar Login
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Nova Votação */}
        <Dialog open={showNovaVotacao} onOpenChange={setShowNovaVotacao}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nova Votação</DialogTitle>
              <DialogDescription>
                Configure a votação após o {tipoVotacao === 'jogo' ? 'jogo' : 'treino'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Tipo de Votação */}
              <div className="space-y-2">
                <Label>Tipo de Votação</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      tipoVotacao === 'treino'
                        ? 'bg-purple-100 border-purple-500'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}
                    onClick={() => setTipoVotacao('treino')}
                  >
                    <Timer className={`h-6 w-6 ${tipoVotacao === 'treino' ? 'text-purple-600' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-semibold">Treino</p>
                      <p className="text-xs text-gray-500">Votação após treino regular</p>
                    </div>
                  </div>
                  <div
                    className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      tipoVotacao === 'jogo'
                        ? 'bg-orange-100 border-orange-500'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}
                    onClick={() => setTipoVotacao('jogo')}
                  >
                    <Swords className={`h-6 w-6 ${tipoVotacao === 'jogo' ? 'text-orange-600' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-semibold">Jogo Amistoso</p>
                      <p className="text-xs text-gray-500">Votação após jogo (2x pontos!)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Campos de Jogo */}
              {tipoVotacao === 'jogo' && (
                <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-semibold text-orange-800 flex items-center gap-2">
                    <Swords className="h-4 w-4" />
                    Informações do Jogo
                  </h4>
                  <div className="space-y-2">
                    <Label>Time Adversário *</Label>
                    <Input
                      value={adversario}
                      onChange={(e) => setAdversario(e.target.value)}
                      placeholder="Ex: Escola XYZ"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Placar (Nossa Equipe)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={placarCasa}
                        onChange={(e) => setPlacarCasa(e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Placar (Adversário)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={placarVisitante}
                        onChange={(e) => setPlacarVisitante(e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Selecionar Turma */}
              <div className="space-y-2">
                <Label>Selecione a Turma</Label>
                <Select value={selectedTurma} onValueChange={handleSelectTurma}>
                  <SelectTrigger>
                    <SelectValue placeholder="Escolha uma turma" />
                  </SelectTrigger>
                  <SelectContent>
                    {turmas.map((turma) => (
                      <SelectItem key={turma.id} value={turma.id}>
                        {turma.nome} - {turma.modalidade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selecionar Categorias */}
              <div className="space-y-2">
                <Label>Selecione as Categorias (máx. 3)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {categorias.map((cat) => (
                    <div
                      key={cat.id}
                      className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedCategorias.includes(cat.id)
                          ? 'bg-purple-100 border-purple-400'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        if (selectedCategorias.length < 3 || selectedCategorias.includes(cat.id)) {
                          toggleCategoria(cat.id);
                        } else {
                          toast.error("Máximo 3 categorias");
                        }
                      }}
                    >
                      <Checkbox checked={selectedCategorias.includes(cat.id)} />
                      <span className="text-sm">{cat.nome}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alunos Presentes */}
              {selectedTurma && alunosTurma.length > 0 && (
                <div className="space-y-2">
                  <Label>Alunos Presentes ({alunosPresentes.length}/{alunosTurma.length})</Label>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2">
                    {alunosTurma.map((aluno) => (
                      <div
                        key={aluno.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded"
                      >
                        <Checkbox
                          checked={alunosPresentes.includes(aluno.id)}
                          onCheckedChange={() => toggleAlunoPresente(aluno.id)}
                        />
                        <span>{aluno.nome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowNovaVotacao(false);
                resetFormVotacao();
              }}>
                Cancelar
              </Button>
              <Button 
                onClick={criarVotacao} 
                className={tipoVotacao === 'jogo' ? "bg-orange-600 hover:bg-orange-700" : "bg-purple-600 hover:bg-purple-700"}
              >
                Criar Votação de {tipoVotacao === 'jogo' ? 'Jogo' : 'Treino'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Resultados */}
        <Dialog open={showResultados} onOpenChange={setShowResultados}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Resultados da Votação</DialogTitle>
              {resultadosSessao && (
                <DialogDescription>
                  {resultadosSessao.sessao.turma_nome} - {new Date(resultadosSessao.sessao.data_treino).toLocaleDateString('pt-BR')}
                </DialogDescription>
              )}
            </DialogHeader>
            
            {resultadosSessao && (
              <div className="space-y-4 py-4">
                {Object.entries(resultadosSessao.resultados).map(([catId, dados]) => (
                  <div key={catId} className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">{dados.categoria_nome}</h4>
                    <div className="space-y-2">
                      {dados.ranking.map((aluno, idx) => (
                        <div 
                          key={aluno.aluno_id}
                          className={`flex justify-between items-center p-2 rounded ${
                            idx === 0 ? 'bg-amber-100' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {idx === 0 && <Trophy className="w-4 h-4 text-amber-500" />}
                            <span>{aluno.nome}</span>
                          </div>
                          <Badge variant={idx === 0 ? "default" : "secondary"}>
                            {aluno.votos} voto{aluno.votos !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <DialogFooter>
              <Button onClick={() => setShowResultados(false)}>Fechar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Votar Professor */}
        <Dialog open={showVotar} onOpenChange={setShowVotar}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Votação do Professor</DialogTitle>
              {sessaoVotando && (
                <DialogDescription>
                  {sessaoVotando.turma_nome} - Vote em cada categoria
                </DialogDescription>
              )}
            </DialogHeader>
            
            {sessaoVotando && (
              <div className="space-y-6 py-4">
                {sessaoVotando.categorias.map((catId) => {
                  const catInfo = categorias.find(c => c.id === catId);
                  return (
                    <div key={catId} className="border rounded-lg p-4">
                      <Label className="text-lg mb-3 block">{catInfo?.nome || catId}</Label>
                      <div className="space-y-2">
                        {alunosTurma.length === 0 ? (
                          <p className="text-sm text-gray-500">Carregue os alunos selecionando a turma</p>
                        ) : (
                          sessaoVotando.alunos_presentes.map((alunoId) => {
                            const aluno = alunosTurma.find(a => a.id === alunoId);
                            return aluno ? (
                              <div
                                key={alunoId}
                                className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  votosProf[catId] === alunoId
                                    ? 'bg-purple-100 border border-purple-400'
                                    : 'hover:bg-gray-50 border border-transparent'
                                }`}
                                onClick={() => registrarVotoProf(catId, alunoId)}
                              >
                                <Checkbox checked={votosProf[catId] === alunoId} />
                                <span>{aluno.nome}</span>
                              </div>
                            ) : null;
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVotar(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={enviarVotosProf}
                disabled={Object.keys(votosProf).length === 0}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Enviar Votos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Novo Aviso */}
        <Dialog open={showNovoAviso} onOpenChange={(open) => { setShowNovoAviso(open); if (!open) resetFormAviso(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-600" />
                Novo Aviso
              </DialogTitle>
              <DialogDescription>
                Envie um comunicado para os responsáveis dos alunos
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Tipo de Aviso */}
              <div className="space-y-2">
                <Label>Tipo de Aviso</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {tiposAviso.map((tipo) => (
                    <div
                      key={tipo.id}
                      className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-center justify-center ${
                        avisoTipo === tipo.id
                          ? 'bg-blue-100 border-blue-500'
                          : 'hover:bg-gray-50 border-gray-200'
                      }`}
                      onClick={() => setAvisoTipo(tipo.id)}
                    >
                      <span className="text-xl">{tipo.icone}</span>
                      <span className="text-sm">{tipo.nome}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Título */}
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={avisoTitulo}
                  onChange={(e) => setAvisoTitulo(e.target.value)}
                  placeholder="Ex: Treino cancelado nesta sexta"
                />
              </div>

              {/* Mensagem */}
              <div className="space-y-2">
                <Label>Mensagem *</Label>
                <Textarea
                  value={avisoMensagem}
                  onChange={(e) => setAvisoMensagem(e.target.value)}
                  placeholder="Escreva sua mensagem aqui..."
                  rows={4}
                />
              </div>

              {/* Turmas (opcional) */}
              <div className="space-y-2">
                <Label>Turmas (deixe vazio para todas)</Label>
                <div className="flex flex-wrap gap-2">
                  {turmas.map((turma) => (
                    <Badge
                      key={turma.id}
                      variant={avisoTurmas.includes(turma.id) ? "default" : "outline"}
                      className={`cursor-pointer ${avisoTurmas.includes(turma.id) ? 'bg-blue-600' : ''}`}
                      onClick={() => toggleTurmaAviso(turma.id)}
                    >
                      {turma.nome}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Detalhes do Evento */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Data do Evento
                  </Label>
                  <Input
                    type="date"
                    value={avisoDataEvento}
                    onChange={(e) => setAvisoDataEvento(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Horário
                  </Label>
                  <Input
                    type="time"
                    value={avisoHorario}
                    onChange={(e) => setAvisoHorario(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Local
                </Label>
                <Input
                  value={avisoLocal}
                  onChange={(e) => setAvisoLocal(e.target.value)}
                  placeholder="Ex: Quadra principal"
                />
              </div>

              {/* Requer Confirmação */}
              <div className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50">
                <Checkbox
                  id="requer-confirmacao"
                  checked={avisoRequerConfirmacao}
                  onCheckedChange={(checked) => setAvisoRequerConfirmacao(checked)}
                />
                <Label htmlFor="requer-confirmacao" className="cursor-pointer">
                  Solicitar confirmação de presença dos responsáveis
                </Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNovoAviso(false)}>
                Cancelar
              </Button>
              <Button onClick={enviarAviso} className="bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4 mr-2" />
                Enviar Aviso
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
